import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/auth/hooks';
import toast from 'react-hot-toast';

interface NewTicketModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const categories = [
  { value: 'Technical', label: 'טכני', icon: '🔧' },
  { value: 'Billing', label: 'חיוב', icon: '💰' },
  { value: 'Feature Request', label: 'הצעת פיצר חדש', icon: '✨' },
  { value: 'Bug Report', label: 'דיווח באג', icon: '🐛' },
  { value: 'General', label: 'כללי', icon: '💬' },
];

const priorities = [
  { value: 'Low', label: 'נמוכה' },
  { value: 'Medium', label: 'בינונית' },
  { value: 'High', label: 'גבוהה' },
  { value: 'Urgent', label: 'דחוף' },
];

export default function NewTicketModal({ onClose, onSuccess }: NewTicketModalProps) {
  const { user, business } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    priority: 'Medium',
    description: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new CustomEvent('ui:support-modal', { detail: { open: true } }));
    return () => {
      document.body.style.overflow = 'unset';
      window.dispatchEvent(new CustomEvent('ui:support-modal', { detail: { open: false } }));
    };
  }, []);

  function validateForm() {
    const newErrors: Record<string, string> = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'יש להזין נושא';
    }

    if (!formData.category) {
      newErrors.category = 'יש לבחור קטגוריה';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'יש להזין תיאור';
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'התיאור חייב להכיל לפחות 20 תווים';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const businessId = business?.id || user?.user_metadata?.business_id;

    if (!businessId) {
      toast.error('חסר מזהה עסק. אנא פנה לתמיכה.');
      return;
    }

    if (!user?.id) {
      toast.error('חסר מזהה משתמש. אנא התחבר מחדש.');
      return;
    }

    try {
      setLoading(true);

      const { data: ticket, error } = await supabase
        .from('support_tickets')
        .insert({
          business_id: businessId,
          subject: formData.subject.trim(),
          category: formData.category,
          priority: formData.priority,
          description: formData.description.trim(),
          created_by_user_id: user.id,
          status: 'Open',
        })
        .select()
        .single();

      if (error) {

        if (error.code === '42501') {
          toast.error('אין לך הרשאה ליצור טיקט. ייתכן שהחשבון שלך לא מוגדר כראוי.');
        } else {
          throw error;
        }
        return;
      }

      toast.success(`הטיקט נוצר בהצלחה! מספר: ${ticket.ticket_number}`);
      onSuccess();
    } catch (error) {

      toast.error('שגיאה ביצירת הטיקט');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="absolute inset-0 flex items-end sm:items-center justify-center pointer-events-none">
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto"
            style={{ height: '85vh', maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-slate-900">פנייה חדשה לתמיכה</h2>
                <p className="text-sm text-slate-600 mt-1">נשמח לעזור לך בכל שאלה או בעיה</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  נושא <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="תאר בקצרה את הבעיה או השאלה"
                  className={`w-full px-4 py-2 border ${
                    errors.subject ? 'border-red-500' : 'border-slate-300'
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {errors.subject && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.subject}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  קטגוריה <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, category: cat.value })}
                      className={`p-4 border-2 rounded-lg text-right transition-all ${
                        formData.category === cat.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{cat.icon}</span>
                        <span className="text-sm font-medium text-slate-900">{cat.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.category}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  עדיפות <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {priorities.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  תיאור הבעיה <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="תאר את הבעיה או השאלה בפירוט. ככל שנספק יותר מידע, נוכל לעזור לך טוב יותר."
                  rows={6}
                  className={`w-full px-4 py-2 border ${
                    errors.description ? 'border-red-500' : 'border-slate-300'
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.description ? (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.description}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500">מינימום 20 תווים</p>
                  )}
                  <p
                    className={`text-sm ${
                      formData.description.length < 20 ? 'text-red-500' : 'text-slate-500'
                    }`}
                  >
                    {formData.description.length} תווים
                  </p>
                </div>
              </div>
            </form>

            <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-medium"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? 'שולח...' : 'שלח פנייה'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}
